import "./Modal.scss";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { GrClose } from "../../assets/svg/svgIcons";
import Button from "../Button/Button";

// App-configurable default label for the built-in cancel button. Host apps call
// `setModalCancelLabel` once at startup (and on locale change) so every modal
// gets a localized label without threading the prop through each call site. A
// per-instance `cancelLabel` prop still overrides this.
let defaultCancelLabel = "Cancel";

export function setModalCancelLabel(label: string): void {
  defaultCancelLabel = label;
}

interface ModalProps {
  modalButton: (props: {
    onClick: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  }) => React.ReactElement;
  modalTitle: string | React.ReactElement;
  modalCloseIcon?: boolean;
  modalContent: React.ReactElement;
  modalGap?: string;
  modalFooterButton:
    | React.ReactElement
    | ((props: { onClose: () => void }) => React.ReactElement);
  modalFooterClass?: string;
  onClick?: () => void;
  modalCancel?: boolean;
  cancelLabel?: string;
  className?: string;
  defaultOpen?: boolean;
  variant?: "default" | "drawer";
}

const Modal: React.FC<ModalProps> = ({
  className,
  modalButton,
  modalTitle,
  modalCloseIcon = false,
  modalContent,
  modalGap = "10px",
  modalFooterButton,
  modalFooterClass,
  onClick,
  modalCancel = true,
  cancelLabel = defaultCancelLabel,
  defaultOpen = false,
  variant = "default",
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(defaultOpen);
  const modalRef = useRef<HTMLDivElement>(null);

  function handleOpenModal(e?: React.MouseEvent<HTMLButtonElement>) {
    if (e) e.stopPropagation(); // Prevent event propagation
    setShowConfirmDialog(true);
    if (onClick) {
      onClick();
    }
  }

  function handleCloseModal() {
    setShowConfirmDialog(false);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.code === "Escape" || event.code === "Delete") {
      handleCloseModal();
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function handleModalClick(event: MouseEvent) {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      event.stopPropagation();
      setShowConfirmDialog(false);
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleModalClick);

    return () => {
      document.removeEventListener("mousedown", handleModalClick);
    };
  }, []);

  const ModalButton = modalButton;
  const variantClassName = variant === "drawer" ? "drawer" : "";
  const footerButtonElement =
    typeof modalFooterButton === "function"
      ? modalFooterButton({ onClose: handleCloseModal })
      : modalFooterButton;

  return (
    <>
      <ModalButton onClick={(e) => handleOpenModal(e)} />

      {createPortal(
        showConfirmDialog && (
          <div
            className={`modalContainer ${variantClassName} ${className ?? ""}`.trim()}
            onClick={(e) => {
              // Stop React synthetic events
              e.stopPropagation();
            }}
          >
            <div
              className={`modal ${variantClassName}`.trim()}
              ref={modalRef}
              style={{ gap: modalGap }}
              onClick={(e) => {
                // Ensure clicks inside modal content don't bubble to parent React tree
                e.stopPropagation();
              }}
            >
              <div className="modalHeader">
                {(modalCloseIcon || !modalTitle || modalTitle === "") && (
                  <GrClose
                    className="grClose"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseModal();
                    }}
                  />
                )}
                {modalTitle !== "" && (
                  <div className="modalTitle">{modalTitle}</div>
                )}
              </div>

              <div className="modalBody">{modalContent}</div>
              {(modalFooterButton || modalCancel) && (
                <div
                  className={`buttonWrapper ${modalFooterClass ? modalFooterClass : ""}`}
                >
                  {footerButtonElement}
                  {modalCancel && (
                    <Button
                      onClick={(e) => {
                        if (e) {
                          e.stopPropagation();
                        }
                        handleCloseModal();
                      }}
                      label={cancelLabel}
                      margin="0 5px 0 auto"
                      style="transparent"
                      height={19}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        ),
        document.getElementById("root")!,
      )}
    </>
  );
};

export default Modal;
